import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AppStoreButton = ({ className = '' }: { className?: string }) => (
  <a
    href="https://apps.apple.com/us/app/tread-ai/id6755405651"
    target="_blank"
    rel="noopener noreferrer"
    className={`inline-flex items-center gap-2.5 px-5 py-3 bg-black text-white rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 ${className}`}
  >
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
    Download on App Store
  </a>
);

const STATS = [
  { value: '10K+', label: 'Tires Analyzed' },
  { value: '<5s', label: 'Analysis Time' },
  { value: '±1.4mm', label: 'Accuracy' },
  { value: '4.8', label: 'App Rating' },
];

const FEATURES = [
  {
    title: 'Tread Depth Measurement',
    desc: 'AI measures tread depth in millimeters with ±1.4mm accuracy using computer vision',
    badge: 'Core',
  },
  {
    title: 'Wear Pattern Detection',
    desc: 'Identifies inner, outer, or center wear that may indicate alignment or inflation issues',
    badge: 'Smart',
  },
  {
    title: 'DOT Code Reading',
    desc: 'OCR extracts manufacturing date from your tire sidewall to calculate tire age',
    badge: 'OCR',
  },
  {
    title: 'Safety Classification',
    desc: 'Each tire rated OK, Watch, Replace Soon, or Unsafe with clear next steps',
    badge: 'Safety',
  },
  {
    title: 'Share Reports',
    desc: 'Export professional PDF reports to share with your mechanic or insurance provider',
    badge: 'New',
  },
  {
    title: 'Dark Mode',
    desc: 'Full dark mode support on the mobile app — follows system setting or manual toggle',
    badge: 'New',
  },
];

const TREAD_THRESHOLDS = [
  { range: '8–6mm', label: 'New / Good', color: 'bg-emerald-500', textColor: 'text-emerald-700', bgColor: 'bg-emerald-50' },
  { range: '5–3mm', label: 'Monitor', color: 'bg-amber-500', textColor: 'text-amber-700', bgColor: 'bg-amber-50' },
  { range: '3–2mm', label: 'Replace Soon', color: 'bg-orange-500', textColor: 'text-orange-700', bgColor: 'bg-orange-50' },
  { range: '<2mm', label: 'Unsafe', color: 'bg-red-500', textColor: 'text-red-700', bgColor: 'bg-red-50' },
];

export const Dashboard = () => {
  const navigate = useNavigate();
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(`TreadAI Inquiry from ${contactForm.name}`);
    const body = encodeURIComponent(`Name: ${contactForm.name}\nEmail: ${contactForm.email}\n\nMessage:\n${contactForm.message}`);
    window.location.href = `mailto:contact@tread-ai.com?subject=${subject}&body=${body}`;
    setFormSubmitted(true);
    setTimeout(() => setFormSubmitted(false), 3000);
  };

  return (
    <div className="space-y-16 md:space-y-24">

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-brand-900 rounded-3xl" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-500/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-500/15 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
        <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] bg-emerald-500/10 rounded-full blur-[80px] -translate-x-1/2 -translate-y-1/2" />

        <div className="relative px-6 sm:px-10 py-14 sm:py-20 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-emerald-300 text-sm font-medium">ML Models Active</span>
              <span className="text-white/30">|</span>
              <span className="text-white/70 text-sm">v1.2.1</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-4 tracking-tight leading-[1.1]">
              Know Your Tires Are Safe
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-400 via-blue-400 to-purple-400">
                In Seconds
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Snap a photo. Get instant AI-powered tread depth measurement, wear analysis, and safety rating. No tools needed.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <AppStoreButton className="w-full sm:w-auto justify-center px-8 py-4 text-lg shadow-xl shadow-black/30 hover:shadow-2xl hover:shadow-black/40" />
              <button
                onClick={() => navigate('/inspect')}
                className="w-full sm:w-auto px-6 py-3.5 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border border-white/20 hover:bg-white/20 transition-all"
              >
                Try Web Inspector
              </button>
            </div>
          </div>

          {/* Stats bar */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {STATS.map((stat) => (
              <div
                key={stat.label}
                className="text-center px-4 py-4 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10"
              >
                <div className="text-xl sm:text-2xl font-extrabold text-white">{stat.value}</div>
                <div className="text-xs sm:text-sm text-slate-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== VIDEO DEMO ===== */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-500/5 via-purple-500/5 to-pink-500/5 rounded-3xl" />
        <div className="relative bg-white/60 backdrop-blur-xl rounded-3xl p-6 sm:p-10 border border-gray-200/60 shadow-xl">
          <div className="text-center mb-8">
            <span className="inline-block text-sm font-bold text-purple-600 tracking-widest uppercase mb-3">Demo</span>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">See TreadAI in Action</h2>
            <p className="text-gray-500">Watch a real inspection from start to finish</p>
          </div>
          <div className="max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-2xl bg-black">
            <video
              className="w-full h-auto"
              controls
              playsInline
              preload="metadata"
            >
              <source src="/tread-ai-demo.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* ===== FEATURES GRID ===== */}
      <section>
        <div className="text-center mb-12">
          <span className="inline-block text-sm font-bold text-emerald-600 tracking-widest uppercase mb-3">Features</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">Everything You Need</h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">Professional-grade tire analysis powered by machine learning</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((feature) => (
            <div key={feature.title} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
              <div className="mb-4">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                  feature.badge === 'New'
                    ? 'bg-brand-100 text-brand-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {feature.badge}
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== TREAD DEPTH GUIDE ===== */}
      <section className="relative">
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900 rounded-3xl p-8 sm:p-12 overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-brand-500/10 rounded-full blur-[100px]" />

          <div className="relative">
            <div className="text-center mb-10">
              <span className="inline-block text-sm font-bold text-brand-400 tracking-widest uppercase mb-3">Safety Guide</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">Tread Depth Thresholds</h2>
              <p className="text-slate-400 text-lg max-w-lg mx-auto">Know when your tires are safe and when it's time to replace</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {TREAD_THRESHOLDS.map((t) => (
                <div key={t.range} className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 text-center">
                  <div className={`w-4 h-4 ${t.color} rounded-full mx-auto mb-3`} />
                  <div className="text-2xl font-extrabold text-white mb-1">{t.range}</div>
                  <div className="text-sm text-slate-400 font-medium">{t.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-slate-400 text-sm max-w-lg mx-auto">
                Tires below 2mm tread depth are illegal in most states and significantly increase stopping distance on wet roads. TreadAI helps you catch wear early.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA BANNER ===== */}
      <section className="group relative">
        <div className="absolute inset-0 bg-gradient-to-r from-brand-500 to-purple-600 rounded-3xl blur-xl opacity-40 group-hover:opacity-60 transition-opacity" />
        <div className="relative bg-gradient-to-r from-brand-600 via-brand-700 to-purple-700 rounded-3xl p-8 sm:p-12 text-center overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0di0xaDR2MWgtNHptLTIgMGgtMnYtMWgydjF6bS0yLTJoMnYtMWgtMnYxem0yLTFoNHYtMWgtNHYxem02LTFoLTJ2MWgydi0xem0wIDJoLTJ2MWgydi0xeiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">
              Ready to Check Your Tires?
            </h2>
            <p className="text-white/80 text-lg mb-6 max-w-md mx-auto">
              Download TreadAI free on the App Store
            </p>
            <AppStoreButton className="justify-center px-8 py-4 text-lg" />
          </div>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section className="bg-white rounded-3xl p-8 sm:p-12 border border-gray-100 shadow-sm">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Left - Info */}
          <div>
            <span className="inline-block text-sm font-bold text-brand-600 tracking-widest uppercase mb-3">Contact</span>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Get in Touch</h2>
            <p className="text-gray-500 leading-relaxed mb-6">
              We're a team of software and ML engineers in California, passionate about making tire safety accessible through AI.
            </p>

            <a
              href="tel:+19495584019"
              className="flex items-center gap-4 p-4 bg-gradient-to-r from-brand-50 to-purple-50 rounded-2xl border border-brand-100 hover:border-brand-300 hover:shadow-lg transition-all group mb-6"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-brand-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <div>
                <div className="text-sm text-gray-500">Call us</div>
                <div className="text-xl font-bold text-gray-900 group-hover:text-brand-700 transition-colors">(949) 558-4019</div>
              </div>
            </a>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              California, USA
            </div>
          </div>

          {/* Right - Form */}
          <div>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-1.5">Name</label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-all outline-none bg-gray-50 focus:bg-white"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-all outline-none bg-gray-50 focus:bg-white"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-1.5">Message</label>
                <textarea
                  id="message"
                  required
                  rows={4}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-all outline-none resize-none bg-gray-50 focus:bg-white"
                  placeholder="How can we help you?"
                />
              </div>
              <button
                type="submit"
                className="w-full px-6 py-3.5 bg-gradient-to-r from-brand-600 to-purple-600 text-white font-semibold rounded-xl hover:from-brand-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                {formSubmitted ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Sent!
                  </span>
                ) : (
                  'Send Message'
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};
